/**
 * Phase 5.5.b — floating toast stack (bottom-right).
 *
 * Reads `useToastStore` and renders each entry with a colored
 * border matching its `kind`. Auto-dismiss is handled per-toast via
 * a scheduled `dismissToast` call; manual dismiss is available via
 * the `×` button on each toast.
 *
 * Mount once in a high-level layout (V2 `CaseWorkspaceLayout`) —
 * this component is layout-agnostic and renders a fixed-position
 * overlay.
 */

import { useEffect } from "react";
import { X } from "lucide-react";
import {
  useToastStore,
  type ToastEntry,
} from "@/stores/useToastStore";
import { cn } from "@/lib/utils";

const DEFAULT_AUTO_DISMISS_MS = 4_000;

export function ToastOverlay() {
  const toasts = useToastStore((s) => s.toasts);
  const dismissToast = useToastStore((s) => s.dismissToast);

  return (
    <div
      data-testid="toast-overlay"
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
    >
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onDismiss={() => dismissToast(toast.id)}
        />
      ))}
    </div>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastEntry;
  onDismiss: () => void;
}) {
  const delay = toast.autoDismissMs ?? DEFAULT_AUTO_DISMISS_MS;
  useEffect(() => {
    if (delay <= 0) return;
    const t = setTimeout(onDismiss, delay);
    return () => clearTimeout(t);
    // Intentionally stable — dismiss is the component's single side
    // effect and we don't want timer churn on re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Semantic accent DOT only — no colored fill, no thick border
  // (plan §6.7). The toast surface stays warm; the dot carries the kind.
  const dotClass =
    toast.kind === "success"
      ? "bg-healthy"
      : toast.kind === "warn"
        ? "bg-warning"
        : toast.kind === "error"
          ? "bg-critical"
          : "bg-cream-400 dark:bg-coffee-500";

  return (
    <div
      data-testid="toast-entry"
      data-toast-kind={toast.kind ?? "info"}
      className="pointer-events-auto flex min-w-[220px] max-w-[360px] items-center gap-2.5 rounded-xl border border-cream-200/60 bg-white px-3 py-2.5 text-body-sm text-coffee-700 shadow-[0_1px_2px_rgba(28,24,20,0.04),0_8px_24px_rgba(28,24,20,0.06)] dark:border-coffee-700/60 dark:bg-coffee-900 dark:text-cream-200"
    >
      <span className={cn("h-2 w-2 shrink-0 rounded-full", dotClass)} aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{toast.text}</span>
      {toast.action && (
        <button
          type="button"
          data-testid="toast-action"
          onClick={() => {
            toast.action!.onAction();
            onDismiss();
          }}
          className="shrink-0 rounded-md px-1.5 py-0.5 text-body-sm font-medium text-gold-600 transition-colors hover:bg-cream-100/60 hover:text-gold-700 dark:text-gold-400 dark:hover:bg-coffee-800/60 dark:hover:text-gold-500"
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 rounded-md p-0.5 text-cream-500 transition-colors hover:bg-cream-100/60 hover:text-coffee-700 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200"
        title="Dispensar"
      >
        <X size={12} />
      </button>
    </div>
  );
}
